import { useMemo, useEffect, useCallback, useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { SlideOutDrawer, Flex, Tabs, Tab, DropdownButton, toast, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Column, ColumnContainer } from "@jsq/ds/layouts";
import { Colors } from "@jsq/ds/styles";
import { withClientContext } from "~/apollo/client";
import { globalEnv } from "~/global-env";
import { useGetCurrentUserQuery } from "~/graphql/mainHooks";
import {
  ComplianceCaseStatus,
  ComplianceCaseTypeEnum,
  ComplianceExternalObjectType,
} from "~/graphql/supergraphEnums";
import {
  useGetCaseInvestingProfileRiskRatingQuery,
  useGetComplianceCaseQuery,
  useGetPositionsQuery,
  useGetUpdatedRiskRatingCountForPositionsQuery,
} from "~/graphql/supergraphHooks";
import CaseDetailsFooter from "~/pages/Compliance/CaseDetailsDrawer/CaseDetailFooter";
import { EditProfileDetailsFullscreenTakeover } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/EditAction/EditProfileDetailsFullscreenTakeover";
import { ViewProfileDetailsFullscreenTakeover } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/ViewProfileDetailsFullscreenTakeover";
import { isEntityClientOnboardingCaseType } from "~/pages/Compliance/ComplianceTable/CaseTypeRenderer/utils";
import JointChildOwner from "~/pages/Compliance/profile/JointChildOwner";
import {
  Programs,
  ProgramName,
  isEscalationRevampCaseType,
} from "~/pages/Compliance/utils/constants";
import { SPACING } from "~/styles/variables";
import {
  ComplianceRulesContext,
  ComplianceRulesContextData,
  getDefaultComplianceRulesContextData,
} from "../complianceRulesContext";
import { CurrentCaseContextProvider } from "../context/CurrentCaseContext";
import {
  ProfileHierarchyProvider,
  useProfileHierarchy,
} from "../context/ProfileHierarchyContext";
import {
  useComplianceEntityOnboardingEnabled,
  useIOReusableProfiles,
  useIsFatcaCrsEnabledForCase,
  usePicrRevampFeatureFlag,
} from "../flags";
import ProfileCreator from "../profile/ProfileCreator";
import { isStandaloneCaseType } from "../utils";
import CaseDetailsHeader from "./CaseDetailsHeader";
import Details from "./Details";
import Documents from "./Documents";
import DocumentManagementFullscreenTakeover from "./Documents/DocumentManagement";
import Escalations from "./Escalations";
import CreateEscalationModal from "./Escalations/CreateEscalationModal";
import EscalationTakeover from "./Escalations/EscalationTakeover";
import { escalationTakeoverPaths } from "./Escalations/EscalationTakeover/paths";
import { FatcaCrs } from "./FatcaCrs";
import Log from "./Log";
import PaymentInstructionChange from "./PaymentInstructionChange";
import PositionsTable from "./PaymentInstructionChange/PositionsTable";
import PaymentInstructions from "./PaymentInstructions";
import Positions from "./Positions";
import Profiles from "./Profiles";
import CreateOwnersAndControllersFullscreenTakeover from "./Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover";
import { PicrScreeningWrapper } from "./Screening/ViewScreeningDetailsFullscreenTakeover/PicrScreeningWrapper";
import { ScreeningWrapper } from "./Screening/ViewScreeningDetailsFullscreenTakeover/ScreeningWrapper";
import { StandaloneFatcaCrs } from "./StandaloneFatcaCrs";
import * as drawerStyles from "./styles_vanilla.css";
import { CaseDetailsPaths } from "./types";
import { getAssigneeRole } from "./utils/getAssigneeRole";
import { isRecurringScreeningComplianceCase } from "./utils/isRecurringScreeningCase";
import { calculatePicrPosition } from "./utils/picrUtils";

const CaseDetailsDrawerContent = () => {
  const history = useHistory();
  const location = useLocation();
  const { url, params } = useRouteMatch<{ caseId: string }>();
  const { isEditingHierarchy } = useProfileHierarchy();

  const caseId = parseInt(params.caseId, 10);
  const isValidCaseId = Number.isFinite(caseId) && caseId > 0;

  const { data: complianceCaseData } = useGetComplianceCaseQuery({
    variables: {
      caseId,
    },
    skip: !isValidCaseId,
    fetchPolicy: "network-only", // Ensures the drawer always loads fresh data, including newly created profiles
  });

  const picrRevampEnabled = usePicrRevampFeatureFlag();
  const ioReusableProfilesEnabled = useIOReusableProfiles();
  const fatcaCrsEnabled = useIsFatcaCrsEnabledForCase(
    complianceCaseData?.complianceCase?.caseType as ComplianceCaseTypeEnum | undefined,
    complianceCaseData?.complianceCase?.entity?.complianceSettings?.fatcaCrsEnabled ?? false,
  );

  const programId = complianceCaseData?.complianceCase?.programId || Programs.US_AML;

  const { data: riskRatingData } = useGetCaseInvestingProfileRiskRatingQuery({
    variables: {
      caseId,
    },
    skip: !isValidCaseId,
  });

  const rules =
    typeof complianceCaseData?.complianceCase?.rules === "string"
      ? (JSON.parse(complianceCaseData?.complianceCase?.rules) as ComplianceRulesContextData)
      : getDefaultComplianceRulesContextData();

  // Override default tab route for payment instruction change cases
  if (
    (complianceCaseData?.complianceCase?.caseType as ComplianceCaseTypeEnum) ===
    ComplianceCaseTypeEnum.PaymentInstructionChangeReview
  ) {
    if (picrRevampEnabled) {
      rules.defaultTabRoute = "payment-instructions";
    } else {
      rules.defaultTabRoute = "payment-instruction-change";
    }
    rules.usesDocs = true;
  }

  // Override default tab route for document refresh cases
  if (
    (complianceCaseData?.complianceCase?.caseType as ComplianceCaseTypeEnum) ===
    ComplianceCaseTypeEnum.DocumentRefresh
  ) {
    rules.defaultTabRoute = "documents";
  }

  // For Escalation with source IO/PICR, default to Details
  if (
    (complianceCaseData?.complianceCase?.caseType as ComplianceCaseTypeEnum) ===
    ComplianceCaseTypeEnum.Escalation
  ) {
    const srcType = complianceCaseData?.complianceCase?.sourceCase?.caseType as
      ComplianceCaseTypeEnum | undefined;
    if (isEscalationRevampCaseType(srcType)) {
      rules.defaultTabRoute = "details";
      rules.showCaseDetails = true;
      rules.showEscalationDetails = true;
    }
  }

  const { data: userData } = useGetCurrentUserQuery({
    context: withClientContext("staff"),
    notifyOnNetworkStatusChange: true,
  });
  const assigneeRole = getAssigneeRole(complianceCaseData, userData);

  const getCaseDetailsPagePath = useCallback((tab: CaseDetailsPaths) => `${url}/${tab}`, [url]);
  const checkIsTabActive = (tab: CaseDetailsPaths) =>
    location.pathname === getCaseDetailsPagePath(tab);

  const resetActiveCaseInView = () => {
    const escalationsBasePath = `${globalEnv.complianceRoute}/escalations`;
    if (location.pathname.startsWith(escalationsBasePath)) {
      history.push(escalationsBasePath);
      return;
    }
    history.push(globalEnv.complianceRoute);
  };

  const screeningExcludedCaseTypes: Array<ComplianceCaseTypeEnum> = useMemo(
    () => [
      ComplianceCaseTypeEnum.GpSourceOfFunds,
      ComplianceCaseTypeEnum.GpTransactionMonitoring,
    ],
    [],
  );

  const subscriptionId = complianceCaseData?.complianceCase?.externalObjects?.find(
    (obj) => obj.objectTypeEnum === ComplianceExternalObjectType.Subscription,
  )?.objectId;

  const showPaymentInstructionChangeTab = useMemo(() => {
    return (
      (complianceCaseData?.complianceCase?.caseType as ComplianceCaseTypeEnum) ===
        ComplianceCaseTypeEnum.PaymentInstructionChangeReview && !picrRevampEnabled
    );
  }, [complianceCaseData?.complianceCase?.caseType, picrRevampEnabled]);

  const currentCaseType = useMemo(() => {
    return complianceCaseData?.complianceCase?.caseType as ComplianceCaseTypeEnum | undefined;
  }, [complianceCaseData?.complianceCase?.caseType]);

  const sourceCaseType = useMemo(() => {
    return complianceCaseData?.complianceCase?.sourceCase?.caseType as
      ComplianceCaseTypeEnum | undefined;
  }, [complianceCaseData?.complianceCase?.sourceCase?.caseType]);

  const showProfilesTab = useMemo(() => {
    if (
      currentCaseType === ComplianceCaseTypeEnum.Escalation &&
      isEscalationRevampCaseType(sourceCaseType)
    ) {
      return false;
    }

    return (
      (rules.usesProfiles ?? false) &&
      currentCaseType !== ComplianceCaseTypeEnum.PaymentInstructionChangeReview
    );
  }, [rules.usesProfiles, currentCaseType, sourceCaseType]);

  const showEscalationsTab = useMemo(() => {
    if (!complianceCaseData?.complianceCase?.caseType) return false;

    const currentCaseType = complianceCaseData.complianceCase
      .caseType as ComplianceCaseTypeEnum;
    const sourceCaseType = complianceCaseData.complianceCase.sourceCase?.caseType as
      ComplianceCaseTypeEnum | undefined;

    if (currentCaseType === ComplianceCaseTypeEnum.Escalation) {
      if (isEscalationRevampCaseType(sourceCaseType)) {
        return false;
      }
    } else if (isEscalationRevampCaseType(currentCaseType)) {
      return false;
    }

    return rules.showEscalationsTab && !screeningExcludedCaseTypes.includes(currentCaseType);
  }, [
    rules.showEscalationsTab,
    complianceCaseData?.complianceCase?.caseType,
    complianceCaseData?.complianceCase?.sourceCase?.caseType,
    screeningExcludedCaseTypes,
  ]);

  const showPaymentInstructionTab = useMemo(() => {
    if (
      currentCaseType === ComplianceCaseTypeEnum.PaymentInstructionChangeReview &&
      picrRevampEnabled
    )
      return true;
    if (isStandaloneCaseType(currentCaseType)) {
      return false;
    }
    return (
      showProfilesTab &&
      programId > Programs.KYC &&
      !isRecurringScreeningComplianceCase(currentCaseType)
    );
  }, [showProfilesTab, programId, currentCaseType, picrRevampEnabled]);

  const showAffectedPositionsTab = useMemo(() => {
    return (
      currentCaseType === ComplianceCaseTypeEnum.PaymentInstructionChangeReview &&
      picrRevampEnabled
    );
  }, [currentCaseType, picrRevampEnabled]);

  const isStandaloneCase = useMemo(
    () => isStandaloneCaseType(currentCaseType),
    [currentCaseType],
  );

  const showPositionsTab =
    isStandaloneCase ||
    (ioReusableProfilesEnabled &&
      (currentCaseType === ComplianceCaseTypeEnum.InvestorOnboarding ||
        currentCaseType === ComplianceCaseTypeEnum.Remediation));

  const showFatcaCrsTab = useMemo(() => {
    if (!fatcaCrsEnabled) return false;
    if (
      currentCaseType === ComplianceCaseTypeEnum.InvestorOnboarding ||
      currentCaseType === ComplianceCaseTypeEnum.Remediation
    )
      return showProfilesTab;
    if (isStandaloneCase) return true;
    return false;
  }, [fatcaCrsEnabled, currentCaseType, showProfilesTab, isStandaloneCase]);

  const { data: standAlonePositionsData } = useGetPositionsQuery({
    variables: { caseId },
    fetchPolicy: "no-cache",
    skip: !showPositionsTab,
  });

  const standAlonePositionsCount = standAlonePositionsData?.casePositionDetails?.length ?? 0;

  // Fetch PICR position details for updated risk rating count
  const { data: picrPositionData } = useGetUpdatedRiskRatingCountForPositionsQuery({
    variables: {
      caseId,
    },

    skip: !showAffectedPositionsTab,
  });

  const positionCountInPICR = calculatePicrPosition(picrPositionData);

  const showHeaderActions = useMemo(() => {
    const type = complianceCaseData?.complianceCase?.caseType as
      ComplianceCaseTypeEnum | undefined;
    return (
      type === ComplianceCaseTypeEnum.InvestorOnboarding ||
      type === ComplianceCaseTypeEnum.PaymentInstructionChangeReview ||
      type === ComplianceCaseTypeEnum.Remediation ||
      (!!type && isEntityClientOnboardingCaseType(type))
    );
  }, [complianceCaseData?.complianceCase?.caseType]);

  // Header actions: Create Escalation modal
  const internalEscalationsEnabled = useComplianceEntityOnboardingEnabled();
  const [isHeaderEscalationModalOpen, setIsHeaderEscalationModalOpen] =
    useState<boolean>(false);
  const openCreateEscalationFromHeader = () => {
    if (internalEscalationsEnabled) {
      history.push(escalationTakeoverPaths.create(caseId), { from: history.location.pathname });
      return;
    }
    setIsHeaderEscalationModalOpen(true);
  };
  const closeCreateEscalationFromHeader = () => setIsHeaderEscalationModalOpen(false);

  const onExportCase = () => {
    // Placeholder: Hook up to export flow if/when available
    toast.info("Export started");
  };

  /**
   * Validates if a given pathname corresponds to an accessible tab
   * Returns false if the tab exists but should not be accessible based on current conditions
   *
   * @param pathname - The current route pathname to validate
   * @returns boolean - Whether the tab should be accessible
   */
  const isTabAccessible = useCallback(
    (pathname: string) => {
      // Map of tab routes to their visibility conditions
      // Each entry represents a tab and its corresponding show/hide condition
      const tabValidations = {
        profiles: showProfilesTab, // Show profiles tab based on rules.usesProfiles and case type
        "fatca-crs": showFatcaCrsTab, // Show FATCA/CRS tab if feature flag enabled and profiles tab visible
        "payment-instruction-change": showPaymentInstructionChangeTab, // Show payment changes for specific case types
        "payment-instructions": showPaymentInstructionTab, // Show payment instructions tab for PICR with feature flag
        positions: showAffectedPositionsTab || showPositionsTab, // Show positions tab for specific case types
        documents: rules.usesDocs, // Show documents tab if enabled in compliance rules
        details: rules.showCaseDetails, // Show details tab if enabled in compliance rules
      };

      // Check each tab's validation rule
      for (const [tab, isEnabled] of Object.entries(tabValidations)) {
        // If we're on this tab's path and it's not enabled, the tab is not accessible
        if (pathname === getCaseDetailsPagePath(tab as CaseDetailsPaths) && !isEnabled) {
          return false;
        }
      }
      // If no validation rules were violated, the tab is accessible
      return true;
    },
    [
      showProfilesTab,
      showFatcaCrsTab,
      showPaymentInstructionChangeTab,
      showPaymentInstructionTab,
      showAffectedPositionsTab,
      showPositionsTab,
      rules.usesDocs,
      rules.showCaseDetails,
      getCaseDetailsPagePath,
    ],
  );

  /**
   * Handles navigation protection and redirection
   * If user attempts to access a disabled tab, redirects them to the default tab
   * This prevents users from accessing tabs that should be hidden based on current state
   */
  const searchParams = useMemo(() => {
    return location.search || window.location.search;
  }, [location.search]);

  useEffect(() => {
    const accessible = isTabAccessible(location.pathname);

    if (complianceCaseData?.complianceCase && !accessible) {
      // redirect to default tab if pointing to unsupported tab
      // Preserve search parameters when redirecting to maintain filter state
      const targetPath = getCaseDetailsPagePath(rules.defaultTabRoute as CaseDetailsPaths);

      history.push({
        pathname: targetPath,
        search: searchParams,
      });
    }
  }, [
    complianceCaseData?.complianceCase, // Only run when we have case data
    location.pathname, // Run when route changes
    searchParams, // Run when search params change
    isTabAccessible, // Run when tab accessibility rules change
    rules.defaultTabRoute, // Run when default route changes
    history, // React Router history object
    getCaseDetailsPagePath, // Path generation function
  ]);

  // noop

  // attach the current case onto rules so footer can read it without prop churn
  const rulesWithCase = useMemo(
    () => ({ ...rules, currentCase: complianceCaseData?.complianceCase }),
    [rules, complianceCaseData?.complianceCase],
  );

  // Redirect to compliance table if caseId is invalid
  if (!isValidCaseId) {
    return <Redirect to="/compliance" />;
  }

  return (
    <CurrentCaseContextProvider
      complianceCase={complianceCaseData?.complianceCase}
      caseId={caseId}
    >
      <ComplianceRulesContext.Provider value={rulesWithCase}>
        <SlideOutDrawer
          anchor="right"
          drawerSize="fluid"
          shouldCloseOnContentInteraction={false}
          isShowing
          headerContent={
            <Flex
              justifyContent="flex-end"
              className={`${drawerStyles.fullWidth} ${drawerStyles.hierarchyEditingState[isEditingHierarchy ? "disabled" : "default"]}`}
              {...(isEditingHierarchy && { inert: "" })}
            >
              {showHeaderActions && (
                <DropdownButton
                  data-testid="case-actions"
                  className={drawerStyles.caseActionsDropdown}
                  variant="muted"
                  icon="ellipsis-h"
                  direction="down"
                  align="right"
                  usePortal
                >
                  <DropdownButton.Action onSelect={onExportCase} disabled>
                    <Flex
                      alignItems="center"
                      gap={8}
                      className={drawerStyles.caseActionsMenuItem}
                    >
                      <Icon type="download" />
                      <Text>Export case</Text>
                    </Flex>
                  </DropdownButton.Action>
                  <DropdownButton.Action
                    onSelect={openCreateEscalationFromHeader}
                    disabled={!assigneeRole || assigneeRole === "checker"}
                  >
                    <Flex
                      alignItems="center"
                      gap={8}
                      className={drawerStyles.caseActionsMenuItem}
                    >
                      <Icon type="arrow-up" />
                      <Text>Create escalation</Text>
                    </Flex>
                  </DropdownButton.Action>
                </DropdownButton>
              )}
            </Flex>
          }
          onClose={resetActiveCaseInView}
          style={{ zIndex: 1039 }} // Setting z-index to 1039 to ensure it appears below the full screen take over.
        >
          <Flex
            flexDirection="column"
            style={{
              maxWidth: "1550px",
              // maxWidth: "calc(87.5vw)" /* 7/8 = 87.5% */,
              /**
               * 52px is the height of the slideout drawer header + its margin bottom (76px)
               * minus this Flex's spacing large margin top offset (24px)
               */
              height: "calc(100vh - 52px)",
              width: "85vw",
              marginTop: `-${SPACING.LARGE}`,
              overflow: "hidden",
            }}
          >
            <div
              className={
                drawerStyles.hierarchyEditingState[isEditingHierarchy ? "disabled" : "default"]
              }
              {...(isEditingHierarchy && { inert: "" })}
            >
              <CaseDetailsHeader
                complianceCase={complianceCaseData?.complianceCase}
                caseRiskRatings={riskRatingData?.complianceCase}
              />
            </div>
            <div
              className={
                drawerStyles.hierarchyEditingState[isEditingHierarchy ? "disabled" : "default"]
              }
              {...(isEditingHierarchy && { inert: "" })}
            >
              <Tabs>
                {rules.showCaseDetails && (
                  <Tab
                    active={checkIsTabActive("details")}
                    text="Details"
                    onClick={() => history.push(getCaseDetailsPagePath("details"))}
                  />
                )}
                {showPaymentInstructionChangeTab && (
                  <Tab
                    active={checkIsTabActive("payment-instruction-change")}
                    text="Payment Change"
                    onClick={() =>
                      history.push(getCaseDetailsPagePath("payment-instruction-change"))
                    }
                  />
                )}
                {showProfilesTab && (
                  <Tab
                    active={checkIsTabActive("profiles")}
                    text="Profiles"
                    onClick={() => history.push(getCaseDetailsPagePath("profiles"))}
                  />
                )}
                {showFatcaCrsTab && (
                  <Tab
                    active={checkIsTabActive("fatca-crs")}
                    text="FATCA/CRS"
                    onClick={() => history.push(getCaseDetailsPagePath("fatca-crs"))}
                  />
                )}
                {showPaymentInstructionTab && (
                  <Tab
                    active={checkIsTabActive("payment-instructions")}
                    text="Payment Instructions"
                    onClick={() => history.push(getCaseDetailsPagePath("payment-instructions"))}
                  />
                )}
                {(showAffectedPositionsTab || showPositionsTab) && (
                  <Tab
                    active={checkIsTabActive("positions")}
                    text="Positions"
                    onClick={() => history.push(getCaseDetailsPagePath("positions"))}
                    counterBadgeProps={{
                      count: showAffectedPositionsTab
                        ? positionCountInPICR
                        : standAlonePositionsCount,
                      className: drawerStyles.CounterBadgeBackground,
                    }}
                  />
                )}
                {showEscalationsTab && (
                  <Tab
                    active={checkIsTabActive("escalations")}
                    text="Escalations"
                    onClick={() => history.push(getCaseDetailsPagePath("escalations"))}
                  />
                )}
                {rules.usesDocs && (
                  <Tab
                    active={checkIsTabActive("documents")}
                    text="Documents"
                    onClick={() => history.push(getCaseDetailsPagePath("documents"))}
                  />
                )}
                <Tab
                  active={checkIsTabActive("log")}
                  text="Log"
                  onClick={() => history.push(getCaseDetailsPagePath("log"))}
                />
              </Tabs>
            </div>
            <div
              style={{
                flex: 1,
                backgroundColor: Colors.Neutral100,
                overflow: "auto",
              }}
            >
              <ColumnContainer style={{ width: "100%" }} hasTopPadding={false}>
                <Column size="fluid" style={{ paddingTop: SPACING.X_LARGE }}>
                  <Switch>
                    {internalEscalationsEnabled && (
                      <Route path={`${getCaseDetailsPagePath("escalations")}/new`} exact>
                        <EscalationTakeover
                          mode="create"
                          caseId={caseId}
                          complianceCase={complianceCaseData?.complianceCase}
                        />
                      </Route>
                    )}
                    {internalEscalationsEnabled && (
                      <Route
                        path={`${getCaseDetailsPagePath("escalations")}/:escalationId(\\d+)/edit`}
                        exact
                      >
                        <EscalationTakeover
                          mode="edit"
                          caseId={caseId}
                          complianceCase={complianceCaseData?.complianceCase}
                        />
                      </Route>
                    )}
                    {internalEscalationsEnabled && (
                      <Route
                        path={`${getCaseDetailsPagePath("escalations")}/:escalationId(\\d+)`}
                        exact
                      >
                        <EscalationTakeover
                          mode="view"
                          caseId={caseId}
                          complianceCase={complianceCaseData?.complianceCase}
                        />
                      </Route>
                    )}
                    <Route path={getCaseDetailsPagePath("details")}>
                      <Details complianceCase={complianceCaseData?.complianceCase} />
                    </Route>
                    <Route path={getCaseDetailsPagePath("payment-instruction-change")}>
                      <PaymentInstructionChange
                        caseId={caseId}
                        readonly={complianceCaseData?.complianceCase?.readonly ?? true}
                        complianceCase={complianceCaseData?.complianceCase}
                      />
                    </Route>
                    <Route path={getCaseDetailsPagePath("profiles/create")}>
                      <ProfileCreator
                        caseId={caseId}
                        parentProfileId={
                          complianceCaseData?.complianceCase?.investingProfile?.id
                        }
                        onExit={() => history.push(getCaseDetailsPagePath("profiles"))}
                      />
                    </Route>
                    <Route path={getCaseDetailsPagePath("profiles/edit")}>
                      <EditProfileDetailsFullscreenTakeover
                        caseId={caseId}
                        programId={programId}
                      />
                    </Route>
                    <Route path={getCaseDetailsPagePath("profiles")}>
                      <Profiles
                        caseId={caseId}
                        programId={programId}
                        complianceCase={complianceCaseData?.complianceCase}
                      />
                    </Route>
                    {showFatcaCrsTab && (
                      <Route path={getCaseDetailsPagePath("fatca-crs")}>
                        {isStandaloneCase ? (
                          <StandaloneFatcaCrs
                            complianceCase={complianceCaseData?.complianceCase ?? null}
                            readonly={complianceCaseData?.complianceCase?.readonly ?? true}
                          />
                        ) : (
                          <FatcaCrs
                            documents={
                              complianceCaseData?.complianceCase?.documents?.map((doc) => ({
                                id: doc.id,
                                name: doc.name,
                              })) ?? []
                            }
                          />
                        )}
                      </Route>
                    )}
                    {showPaymentInstructionTab && (
                      <Route path={getCaseDetailsPagePath("payment-instructions")}>
                        <PaymentInstructions
                          caseId={caseId}
                          caseType={
                            (complianceCaseData?.complianceCase
                              ?.caseType as ComplianceCaseTypeEnum) ??
                            ComplianceCaseTypeEnum.InvestorOnboarding
                          }
                          programId={programId}
                          readonly={complianceCaseData?.complianceCase?.readonly ?? true}
                          subscriptionId={subscriptionId}
                        />
                      </Route>
                    )}
                    {showAffectedPositionsTab && (
                      <Route path={getCaseDetailsPagePath("positions")}>
                        <PositionsTable
                          readonly={complianceCaseData?.complianceCase?.readonly ?? true}
                          caseId={caseId}
                          complianceCase={complianceCaseData?.complianceCase}
                        />
                      </Route>
                    )}
                    {showPositionsTab && (
                      <Route path={getCaseDetailsPagePath("positions")}>
                        <Positions />
                      </Route>
                    )}
                    {showEscalationsTab && (
                      <Route path={getCaseDetailsPagePath("escalations")}>
                        <Escalations
                          complianceCase={complianceCaseData?.complianceCase}
                          assigneeRole={assigneeRole}
                        />
                      </Route>
                    )}
                    <Route path={getCaseDetailsPagePath("documents")}>
                      <Documents
                        caseId={caseId}
                        caseType={
                          complianceCaseData?.complianceCase?.caseType as ComplianceCaseTypeEnum
                        }
                        programName={
                          complianceCaseData?.complianceCase?.program?.name as ProgramName
                        }
                        readonly={complianceCaseData?.complianceCase?.readonly ?? true}
                      />
                    </Route>
                    <Route path={getCaseDetailsPagePath("document-management")}>
                      <DocumentManagementFullscreenTakeover />
                    </Route>
                    <Route path={getCaseDetailsPagePath("log")}>
                      <Log caseId={caseId} />
                    </Route>
                    <Route path={getCaseDetailsPagePath("create-owners-and-controllers")}>
                      <CreateOwnersAndControllersFullscreenTakeover
                        caseId={caseId}
                        investingProfileId={
                          complianceCaseData?.complianceCase?.investingProfile?.id
                        }
                      />
                    </Route>
                    <Route path={getCaseDetailsPagePath("create-individual-owner")}>
                      <JointChildOwner
                        caseId={caseId}
                        investingProfileId={
                          complianceCaseData?.complianceCase?.investingProfile?.id
                        }
                      />
                    </Route>
                    <Route path={`${getCaseDetailsPagePath("profile-details")}`}>
                      <ViewProfileDetailsFullscreenTakeover
                        caseId={caseId}
                        programId={programId}
                      />
                    </Route>
                    <Route path={`${getCaseDetailsPagePath("edit-profile")}`}>
                      <EditProfileDetailsFullscreenTakeover
                        caseId={caseId}
                        programId={programId}
                      />
                    </Route>
                    <Route path={`${getCaseDetailsPagePath("screening-details")}`}>
                      <ScreeningWrapper
                        caseId={caseId}
                        complianceCase={complianceCaseData?.complianceCase}
                        readonly={complianceCaseData?.complianceCase?.readonly ?? true}
                      />
                    </Route>
                    {currentCaseType ===
                      ComplianceCaseTypeEnum.PaymentInstructionChangeReview && (
                      <Route path={`${getCaseDetailsPagePath("positions-screening-details")}`}>
                        <PicrScreeningWrapper
                          caseId={caseId}
                          readonly={complianceCaseData?.complianceCase?.readonly ?? true}
                        />
                      </Route>
                    )}
                    {complianceCaseData?.complianceCase && (
                      <Redirect
                        to={getCaseDetailsPagePath(rules.defaultTabRoute as CaseDetailsPaths)}
                      />
                    )}
                  </Switch>
                </Column>
              </ColumnContainer>
            </div>
            <div
              className={
                drawerStyles.hierarchyEditingState[isEditingHierarchy ? "disabled" : "default"]
              }
              {...(isEditingHierarchy && { inert: "" })}
            >
              <CaseDetailsFooter
                assigneeRole={assigneeRole}
                assignee={
                  complianceCaseData?.complianceCase?.assignee?.externalUser?.name || undefined
                }
                caseId={caseId}
                caseIsClosed={complianceCaseData?.complianceCase?.status === "closed"}
                caseStatus={complianceCaseData?.complianceCase?.status}
                caseSubstatus={complianceCaseData?.complianceCase?.subStatus}
                caseType={
                  (complianceCaseData?.complianceCase?.caseType as ComplianceCaseTypeEnum) ??
                  "investor_onboarding"
                }
                programId={programId}
                escalation={complianceCaseData?.complianceCase?.escalations?.[0]}
                escalationsCount={complianceCaseData?.complianceCase?.escalations?.length}
                escalations={complianceCaseData?.complianceCase?.escalations}
                profilesForCase={complianceCaseData?.complianceCase}
                caseResolution={complianceCaseData?.complianceCase?.resolution}
              />
            </div>
            {isHeaderEscalationModalOpen && (
              <CreateEscalationModal
                show={isHeaderEscalationModalOpen}
                onCloseModal={closeCreateEscalationFromHeader}
                caseType={complianceCaseData?.complianceCase?.caseType ?? undefined}
                profileId={complianceCaseData?.complianceCase?.investingProfile?.id ?? 0}
                caseStatus={
                  (complianceCaseData?.complianceCase?.status as ComplianceCaseStatus) ??
                  ComplianceCaseStatus.Ready
                }
                profilesForCase={complianceCaseData?.complianceCase}
              />
            )}
          </Flex>
        </SlideOutDrawer>
      </ComplianceRulesContext.Provider>
    </CurrentCaseContextProvider>
  );
};

const CaseDetailsDrawer = () => {
  return (
    <ProfileHierarchyProvider>
      <CaseDetailsDrawerContent />
    </ProfileHierarchyProvider>
  );
};

export default CaseDetailsDrawer;
